import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  FormControlLabel,
  Switch
} from "@mui/material";
import { getDeliveryConfig, updateDeliveryConfig } from "../services/deliveryConfigService";
import { useAdminScope } from "../contexts/AdminScopeContext";

type Props = {
  open: boolean;
  onClose: () => void;
};

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

const DeliveryOfferSettingsDialog: React.FC<Props> = ({ open, onClose }) => {
  const { selectedGroupId } = useAdminScope();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Server-driven values (we edit in seconds)
  const [offerSeconds, setOfferSeconds] = useState<number>(60);
  const [bufferSeconds, setBufferSeconds] = useState<number>(20);

  // New: TTS / Vibration settings (driver foreground announce)
  const [ttsEnabled, setTtsEnabled] = useState<boolean>(true);
  const [ttsIntervalSeconds, setTtsIntervalSeconds] = useState<number>(8);
  const [ttsFirstDelaySeconds, setTtsFirstDelaySeconds] = useState<number>(2);
  const [vibrateEnabled, setVibrateEnabled] = useState<boolean>(true);
  const [vibrateIntervalSeconds, setVibrateIntervalSeconds] = useState<number>(10);

  // Bounds (policy)
  const OFFER_MIN_SEC = 30; // 0.5 min as allowed by CF
  const OFFER_MAX_SEC = 1800; // 30 min upper bound as allowed by CF
  const BUFFER_MIN_SEC = 5;
  const BUFFER_MAX_SEC = 30;

  // New clamps for TTS/vibration settings
  const TTS_INTERVAL_MIN = 5;
  const TTS_INTERVAL_MAX = 20;
  const TTS_FIRST_DELAY_MIN = 0;
  const TTS_FIRST_DELAY_MAX = 5;
  const VIBRATE_INTERVAL_MIN = 5;
  const VIBRATE_INTERVAL_MAX = 30;
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!open) return;
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const cfg = await getDeliveryConfig(selectedGroupId);
        const timeoutMinutes = typeof cfg.timeoutMinutes === "number" ? cfg.timeoutMinutes : 1;
        const currentOfferSeconds = Math.round(timeoutMinutes * 60);
        const currentBuffer = (cfg as any).networkLatencyBufferSeconds;
        const currentBufferSeconds = typeof currentBuffer === "number" ? currentBuffer : 20;

        if (!cancelled) {
          setOfferSeconds(currentOfferSeconds);
          setBufferSeconds(currentBufferSeconds);

          // New fields with safe defaults
          const ttsEn = (cfg as any).ttsEnabled;
          const ttsInt = (cfg as any).ttsIntervalSeconds;
          const ttsDelay = (cfg as any).ttsFirstDelaySeconds;
          const vibEn = (cfg as any).vibrateEnabled;
          const vibInt = (cfg as any).vibrateIntervalSeconds;

          setTtsEnabled(typeof ttsEn === "boolean" ? ttsEn : true);
          setTtsIntervalSeconds(typeof ttsInt === "number" ? ttsInt : 8);
          setTtsFirstDelaySeconds(typeof ttsDelay === "number" ? ttsDelay : 2);
          setVibrateEnabled(typeof vibEn === "boolean" ? vibEn : true);
          setVibrateIntervalSeconds(typeof vibInt === "number" ? vibInt : 10);

        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Erreur lors du chargement de la configuration.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, selectedGroupId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Clamp on UI before sending
      const offerSecClamped = clamp(Math.round(offerSeconds), OFFER_MIN_SEC, OFFER_MAX_SEC);
      const bufferSecClamped = clamp(Math.round(bufferSeconds), BUFFER_MIN_SEC, BUFFER_MAX_SEC);
      const ttsIntClamped = clamp(Math.round(ttsIntervalSeconds), TTS_INTERVAL_MIN, TTS_INTERVAL_MAX);
      const ttsDelayClamped = clamp(Math.round(ttsFirstDelaySeconds), TTS_FIRST_DELAY_MIN, TTS_FIRST_DELAY_MAX);
      const vibIntClamped = clamp(Math.round(vibrateIntervalSeconds), VIBRATE_INTERVAL_MIN, VIBRATE_INTERVAL_MAX);

      // Map to CF expected fields
      const timeoutMinutes = offerSecClamped / 60;

      await updateDeliveryConfig({
        timeoutMinutes,
        // CF expects seconds directly for the buffer
        // @ts-ignore extend type at runtime
        networkLatencyBufferSeconds: bufferSecClamped,

        // New: driver foreground announce controls
        // @ts-ignore extend type at runtime
        ttsEnabled: !!ttsEnabled,
        // @ts-ignore extend type at runtime
        ttsIntervalSeconds: ttsIntClamped,
        // @ts-ignore extend type at runtime
        ttsFirstDelaySeconds: ttsDelayClamped,
        // @ts-ignore extend type at runtime
        vibrateEnabled: !!vibrateEnabled,
        // @ts-ignore extend type at runtime
        vibrateIntervalSeconds: vibIntClamped,
        // @ts-ignore disabled until offerReminderTask is deployed and revalidated
        enableOfferReminders: false,
      }, {
        groupId: selectedGroupId,
      });

      setSuccess("Paramètres enregistrés avec succès.");
      // Optionally close after success
      setTimeout(() => {
        onClose();
      }, 600);
    } catch (e: any) {
      setError(e?.message || "Échec de la mise à jour de la configuration.");
    } finally {
      setSaving(false);
    }
  };

  const disabled = useMemo(() => loading || saving, [loading, saving]);

  return (
    <Dialog open={open} onClose={disabled ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Paramètres des Offres Livreurs</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            {selectedGroupId && (
              <Alert severity="info">
                Les modifications s’appliquent uniquement au groupe actif. Change de groupe si tu
                veux configurer une autre flotte.
              </Alert>
            )}

            <Typography variant="subtitle1" fontWeight={600}>
              Durées et Buffer (pilotent les offres envoyées aux livreurs)
            </Typography>

            <TextField
              label="Durée de l'offre (secondes)"
              type="number"
              inputProps={{ min: OFFER_MIN_SEC, max: OFFER_MAX_SEC, step: 5 }}
              value={offerSeconds}
              onChange={(e) => setOfferSeconds(Number(e.target.value))}
              disabled={disabled}
              helperText={`Entre ${OFFER_MIN_SEC} et ${OFFER_MAX_SEC} secondes. Ex: 60s aujourd'hui.`}
              fullWidth
            />

            <TextField
              label="Buffer réseau (secondes)"
              type="number"
              inputProps={{ min: BUFFER_MIN_SEC, max: BUFFER_MAX_SEC, step: 1 }}
              value={bufferSeconds}
              onChange={(e) => setBufferSeconds(Number(e.target.value))}
              disabled={disabled}
              helperText={`Entre ${BUFFER_MIN_SEC} et ${BUFFER_MAX_SEC} secondes. Ex: 20s aujourd'hui.`}
              fullWidth
            />

            {/* New: Driver foreground announce (TTS + vibration) */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
              Annonce vocale et vibration (app livreur, écran au premier plan)
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={ttsEnabled}
                  onChange={(_, v) => setTtsEnabled(v)}
                  disabled={disabled}
                />
              }
              label="Activer annonce vocale (TTS)"
            />

            <TextField
              label="Intervalle TTS (secondes)"
              type="number"
              inputProps={{ min: TTS_INTERVAL_MIN, max: TTS_INTERVAL_MAX, step: 1 }}
              value={ttsIntervalSeconds}
              onChange={(e) => setTtsIntervalSeconds(Number(e.target.value))}
              disabled={disabled || !ttsEnabled}
              helperText={`Entre ${TTS_INTERVAL_MIN} et ${TTS_INTERVAL_MAX} secondes. Ex: 8s.`}
              fullWidth
            />

            <TextField
              label="Délai première annonce (secondes)"
              type="number"
              inputProps={{ min: TTS_FIRST_DELAY_MIN, max: TTS_FIRST_DELAY_MAX, step: 1 }}
              value={ttsFirstDelaySeconds}
              onChange={(e) => setTtsFirstDelaySeconds(Number(e.target.value))}
              disabled={disabled || !ttsEnabled}
              helperText={`Entre ${TTS_FIRST_DELAY_MIN} et ${TTS_FIRST_DELAY_MAX} secondes. Ex: 2s.`}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={vibrateEnabled}
                  onChange={(_, v) => setVibrateEnabled(v)}
                  disabled={disabled}
                />
              }
              label="Activer vibration périodique"
            />

            <TextField
              label="Intervalle vibration (secondes)"
              type="number"
              inputProps={{ min: VIBRATE_INTERVAL_MIN, max: VIBRATE_INTERVAL_MAX, step: 1 }}
              value={vibrateIntervalSeconds}
              onChange={(e) => setVibrateIntervalSeconds(Number(e.target.value))}
              disabled={disabled || !vibrateEnabled}
              helperText={`Entre ${VIBRATE_INTERVAL_MIN} et ${VIBRATE_INTERVAL_MAX} secondes. Ex: 10s.`}
              fullWidth
            />

            <Box mt={1}>
              <Typography variant="body2" color="text.secondary">
                Règles d’alignement:
              </Typography>
              <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                <li>Le driver affiche la durée d'offre (offerExpiresInSeconds).</li>
                <li>Le serveur expire à (offre + buffer).</li>
                <li>Le TTL FCM est aligné à l'expiration serveur.</li>
              </ul>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>Annuler</Button>
        <Button onClick={handleSave} variant="contained" disabled={disabled}>
          {saving ? <><CircularProgress size={18} sx={{ mr: 1 }} />Enregistrement...</> : "Enregistrer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliveryOfferSettingsDialog;
